import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  ACHIEVEMENTS,
  ACHIEVEMENT_CATEGORIES,
  type AchievementDefinition,
  type AchievementWithStatus,
  type AchievementCategory,
  type UserAchievement,
} from '@/lib/achievements';

/**
 * Pure function: given server counters + profile status, returns all achievement keys
 * whose conditions are met. Exported for testing.
 */
export function getEligibleKeys(c: Record<string, number>, profileComplete: boolean): string[] {
  const keys: string[] = [];

  // First Steps
  keys.push('first_login'); // user exists = has logged in
  if ((c.accounts ?? 0) >= 1) keys.push('first_account');
  if ((c.transactions ?? 0) >= 1) keys.push('first_transaction');
  if ((c.income ?? 0) >= 1) keys.push('first_income');
  if ((c.expenses ?? 0) >= 1) keys.push('first_expense');
  if ((c.transfers ?? 0) >= 1) keys.push('first_transfer');
  if ((c.custom_categories ?? 0) >= 1) keys.push('first_category');
  if (profileComplete) keys.push('profile_completed');

  // Budget Organization
  if ((c.goals ?? 0) >= 1) keys.push('first_goal');
  if ((c.goal_deposits ?? 0) >= 1) keys.push('first_goal_deposit');
  if ((c.recurring ?? 0) >= 1) keys.push('first_recurring');
  if ((c.transactions ?? 0) >= 5 && (c.accounts ?? 0) >= 2) keys.push('structured_budget');
  if ((c.accounts ?? 0) >= 1 && (c.transactions ?? 0) >= 1 && (c.categorized_expenses ?? 0) >= 1)
    keys.push('complete_view');

  // Regularity
  if ((c.transactions ?? 0) >= 3) keys.push('transactions_3');
  if ((c.transactions ?? 0) >= 10) keys.push('transactions_10');
  if ((c.transactions ?? 0) >= 25) keys.push('transactions_25');
  if ((c.transactions ?? 0) >= 50) keys.push('transactions_50');
  if ((c.transactions ?? 0) >= 100) keys.push('transactions_100');
  if ((c.recurring ?? 0) >= 3) keys.push('recurring_3');
  if ((c.goals ?? 0) >= 5) keys.push('goals_5');

  // Financial Control
  if ((c.categorized_expenses ?? 0) >= 10) keys.push('categorized_expenses');
  if ((c.accounts ?? 0) >= 3) keys.push('organized_accounts');
  if ((c.accounts ?? 0) >= 3 && (c.transactions ?? 0) >= 20 && (c.categorized_expenses ?? 0) >= 5)
    keys.push('clear_vision');

  return keys;
}

export function useAchievements() {
  const { user } = useAuth();
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [counters, setCounters] = useState<Record<string, number>>({});
  const [countersLoaded, setCountersLoaded] = useState(false);

  // Load user achievements from Supabase
  const loadAchievements = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false });

      if (error) throw error;
      setUserAchievements((data as UserAchievement[]) || []);
    } catch (err) {
      console.error('Error loading achievements:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load counters for progress tracking (full historical scan)
  const loadCounters = useCallback(async () => {
    if (!user) return;
    setCountersLoaded(false);
    try {
      const [
        { count: transactionCount },
        { count: accountCount },
        { count: recurringCount },
        { count: categorizedExpenseCount },
        { count: incomeCount },
        { count: expenseCount },
        { count: transferCount },
        { count: goalDepositCount },
        { count: customCategoryCount },
      ] = await Promise.all([
        supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('accounts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('recurring_transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_active', true),
        supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('type', 'expense')
          .not('category_id', 'is', null),
        supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('type', 'income'),
        supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('type', 'expense'),
        supabase
          .from('transactions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('type', 'transfer'),
        supabase
          .from('goal_deposits')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('categories')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id),
      ]);

      // Goals are stored in user_metadata, not in a table
      let goalCount = 0;
      try {
        const {
          data: { user: supaUser },
        } = await supabase.auth.getUser();
        const goals = supaUser?.user_metadata?.goals;
        if (Array.isArray(goals)) {
          goalCount = goals.filter((g: { status?: string }) => g.status === 'active').length;
        }
      } catch {
        /* ignore */
      }

      setCounters({
        transactions: transactionCount ?? 0,
        accounts: accountCount ?? 0,
        recurring: recurringCount ?? 0,
        goals: goalCount,
        categorized_expenses: categorizedExpenseCount ?? 0,
        income: incomeCount ?? 0,
        expenses: expenseCount ?? 0,
        transfers: transferCount ?? 0,
        goal_deposits: goalDepositCount ?? 0,
        custom_categories: customCategoryCount ?? 0,
      });
      setCountersLoaded(true);
    } catch (err) {
      console.error('Error loading achievement counters:', err);
    }
  }, [user]);

  useEffect(() => {
    loadAchievements();
    loadCounters();
  }, [loadAchievements, loadCounters]);

  // Build a lookup map for O(1) access
  const userAchievementMap = useMemo(() => {
    const map = new Map<string, UserAchievement>();
    for (const a of userAchievements) {
      map.set(a.achievement_key, a);
    }
    return map;
  }, [userAchievements]);

  // Unlock an achievement
  const unlockAchievement = useCallback(
    async (key: string): Promise<boolean> => {
      if (!user) return false;
      // Already unlocked?
      if (userAchievementMap.has(key)) return false;

      try {
        const { data, error } = await supabase
          .from('user_achievements')
          .insert({ user_id: user.id, achievement_key: key })
          .select()
          .single();

        if (error) {
          // Duplicate — already unlocked (race condition)
          if (error.code === '23505') return false;
          throw error;
        }

        setUserAchievements((prev) => [data as UserAchievement, ...prev]);
        return true;
      } catch (err) {
        console.error('Error unlocking achievement:', err);
        return false;
      }
    },
    [user, userAchievementMap]
  );

  // Mark achievements as seen
  const markAsSeen = useCallback(async () => {
    if (!user) return;
    const unseenIds = userAchievements.filter((a) => !a.seen).map((a) => a.id);
    if (unseenIds.length === 0) return;

    try {
      await supabase.from('user_achievements').update({ seen: true }).in('id', unseenIds);

      setUserAchievements((prev) =>
        prev.map((a) => (unseenIds.includes(a.id) ? { ...a, seen: true } : a))
      );
    } catch (err) {
      console.error('Error marking achievements as seen:', err);
    }
  }, [user, userAchievements]);

  // Compute progress for count-based achievements
  const getProgress = useCallback(
    (def: AchievementDefinition): number | undefined => {
      if (!def.targetCount) return undefined;

      let current = 0;
      switch (def.key) {
        case 'transactions_3':
        case 'transactions_10':
        case 'transactions_25':
        case 'transactions_50':
        case 'transactions_100':
          current = counters.transactions ?? 0;
          break;
        case 'recurring_3':
          current = counters.recurring ?? 0;
          break;
        case 'goals_5':
          current = counters.goals ?? 0;
          break;
        case 'categorized_expenses':
          current = counters.categorized_expenses ?? 0;
          break;
        case 'organized_accounts':
          current = counters.accounts ?? 0;
          break;
        default:
          return undefined;
      }
      return Math.min(current / def.targetCount, 1);
    },
    [counters]
  );

  // Enriched achievements with status
  const achievementsWithStatus: AchievementWithStatus[] = useMemo(() => {
    return ACHIEVEMENTS.map((def) => {
      const userAch = userAchievementMap.get(def.key);

      if (userAch) {
        // Check if it was unlocked recently (last 24h) and not yet seen
        const isNew =
          !userAch.seen ||
          Date.now() - new Date(userAch.unlocked_at).getTime() < 24 * 60 * 60 * 1000;

        return {
          ...def,
          status: isNew ? ('newly_unlocked' as const) : ('unlocked' as const),
          unlockedAt: userAch.unlocked_at,
          progress: 1,
        };
      }

      const progress = getProgress(def);
      return {
        ...def,
        status:
          progress !== undefined && progress > 0 ? ('in_progress' as const) : ('locked' as const),
        progress,
      };
    });
  }, [userAchievementMap, getProgress]);

  // Stats
  const stats = useMemo(() => {
    const unlocked = achievementsWithStatus.filter(
      (a) => a.status === 'unlocked' || a.status === 'newly_unlocked'
    ).length;
    const total = ACHIEVEMENTS.length;
    const unseenCount = userAchievements.filter((a) => !a.seen).length;

    // Find next achievement closest to completion
    const inProgress = achievementsWithStatus
      .filter((a) => a.status === 'in_progress' && a.progress !== undefined)
      .sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0));

    const nextAchievement = inProgress[0] || null;

    return { unlocked, total, unseenCount, nextAchievement };
  }, [achievementsWithStatus, userAchievements]);

  // Group by category
  const achievementsByCategory = useMemo(() => {
    const categories = Object.keys(ACHIEVEMENT_CATEGORIES) as AchievementCategory[];
    return categories.map((cat) => ({
      category: cat,
      ...ACHIEVEMENT_CATEGORIES[cat],
      achievements: achievementsWithStatus.filter((a) => a.category === cat),
    }));
  }, [achievementsWithStatus]);

  // Recently unlocked (for dropdown)
  const recentlyUnlocked = useMemo(() => {
    return achievementsWithStatus
      .filter((a) => a.status === 'unlocked' || a.status === 'newly_unlocked')
      .sort((a, b) => new Date(b.unlockedAt!).getTime() - new Date(a.unlockedAt!).getTime())
      .slice(0, 5);
  }, [achievementsWithStatus]);

  const refresh = useCallback(() => {
    loadAchievements();
    loadCounters();
  }, [loadAchievements, loadCounters]);

  return {
    loading,
    counters,
    countersLoaded,
    achievements: achievementsWithStatus,
    achievementsByCategory,
    recentlyUnlocked,
    stats,
    unlockAchievement,
    markAsSeen,
    refresh,
  };
}
